import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo, useState } from 'react';
import TrashIcon from '@/assets/trash.svg';
import UnlinkIcon from '@/assets/unlink.svg';
import ViewIcon from '@/assets/view.svg';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerProps } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { StatusBadge } from '@/components/ui/status-badge';
import { strings } from '@/constants/strings';
import { useUnassociatedDevices } from '@/hooks/use-devices';
import { useOpenState } from '@/hooks/use-open-state';
import { useOrganisations } from '@/hooks/use-organisations';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { ViewDevice } from '@/pages/authenticated/groups/group/unit/drawer-views/view-device';
import {
  useAddDeviceToOrganisationMutation,
  useDeleteDeviceMutation,
  useRemoveDeviceFromOrganisationMutation,
} from '@/redux/api/endpoints/device';
import { composeTableOptions } from '@/table';
import { AnyDevice, DeviceAssignedToOrganisation } from '@/types/device';
import { cx } from '@/utils/cx';
import { AssignDevicesForm } from './drawer-views/assign-devices-form';
import { SelectedDevicesActionBar } from './selected-devices-action-bar';

const hasOrganisation = (d: AnyDevice): d is DeviceAssignedToOrganisation =>
  d.organisation !== undefined;

const columns: MRT_ColumnDef<AnyDevice>[] = [
  {
    accessorKey: 'serialNumber',
    header: 'Device Serial Number (DSN)',
    size: 150,
  },
  {
    accessorKey: 'model',
    header: 'Model',
    filterVariant: 'multi-select',
    size: 150,
  },
  {
    id: 'organisation',
    accessorFn: (row) =>
      'organisation' in row ? row.organisation?.name : undefined,
    header: 'Assigned Organisation',
    size: 150,
    filterVariant: 'multi-select',
    Cell: ({ renderedCellValue, row }) => {
      const isAssigned =
        'organisation' in row.original && Boolean(row.original.organisation);
      return (
        <div className={cx(!isAssigned && 'italic')}>
          {isAssigned ? renderedCellValue : 'Not Assigned'}
        </div>
      );
    },
  },
  {
    id: 'status',
    accessorFn: ({ connectivity }) => connectivity?.status,
    header: 'Status',
    filterVariant: 'multi-select',
    grow: false,
    size: 100,
    Cell: ({ row }) => {
      const connectivityStatus = row.original.connectivity?.status ?? 'Unknown';
      return (
        <>
          {'verifiedASP' in row.original &&
          row.original.verifiedASP === 'error' ? (
            <StatusBadge text="Error" variant="warning" />
          ) : (
            <StatusBadge
              text={connectivityStatus}
              variant={connectivityStatus === 'Online' ? 'success' : 'alert'}
            />
          )}
        </>
      );
    },
  },
];

type DevicesContext = { devices: AnyDevice[] };

type DrawerView = 'link' | 'view';

type DrawerContext = { view: DrawerView } & DevicesContext;

export const PlatformDevicesPage: FC = () => {
  const { data: devices, isLoading: isLoadingDevices } = useUnassociatedDevices(); // prettier-ignore
  const { data: organisations } = useOrganisations();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const {
    state: drawerState,
    open: openDrawer,
    toggleOpen: toggleDrawerOpen,
  } = useOpenState<DrawerContext>({ view: 'view', devices: [] });
  const {
    state: unassignDeviceConfirmationState,
    open: openUnassignDeviceConfirmation,
    toggleOpen: toggleUnassignDeviceConfirmationOpen,
  } = useOpenState<DevicesContext>({ devices: [] });
  const {
    state: deleteDeviceConfirmationState,
    open: openDeleteDeviceConfirmation,
    toggleOpen: toggleDeleteDeviceConfirmationOpen,
  } = useOpenState<{ device: AnyDevice | null }>({ device: null });

  const [addDeviceToOrganisation] = useAddDeviceToOrganisationMutation();
  const [removeDeviceFromOrganisation] = useRemoveDeviceFromOrganisationMutation(); // prettier-ignore
  const [deleteDevice] = useDeleteDeviceMutation();

  const selectedDevices = useMemo<AnyDevice[]>(
    () =>
      (Object.keys(rowSelection) as unknown[] as number[]).map(
        (rowIndex) => devices[rowIndex],
      ),
    [rowSelection, devices],
  );

  const table = useMaterialReactTable({
    ...composeTableOptions<AnyDevice>({
      enableRowActions: true,
      enableRowSelection: (row) =>
        row.original.connectivity?.status === 'Online',
    }),
    columns,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    data: devices,
    state: {
      isLoading: isLoadingDevices,
      showColumnFilters: true,
      rowSelection,
      ...(isLoadingDevices
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: setRowSelection,
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<ViewIcon />}
        key="view"
        label="View"
        onClick={() => {
          closeMenu();
          openDrawer({ view: 'view', devices: [row.original] });
        }}
        table={table}
      />,
      // TODO: helper for this?
      'organisation' in row.original && !!row.original.organisation ? (
        <MRT_ActionMenuItem
          icon={<UnlinkIcon />}
          key="unlink"
          label="Unassign"
          onClick={() => {
            closeMenu();
            openUnassignDeviceConfirmation({ devices: [row.original] });
          }}
          table={table}
        />
      ) : null,
      <MRT_ActionMenuItem
        icon={<TrashIcon />}
        key="delete"
        label="Delete"
        onClick={() => {
          closeMenu();
          openDeleteDeviceConfirmation({ device: row.original });
        }}
        table={table}
      />,
    ],
  });

  const getDrawerTitleProps = (): Pick<DrawerProps, 'title' | 'subtitle'> => {
    switch (drawerState.context.view) {
      case 'link':
        return {
          title: strings.devices.drawer_assign.title,
          subtitle: strings.devices.drawer_assign.subtitle,
        };
      case 'view':
        return {
          title: strings.devices.drawer_view.title,
        };
      default:
        return {
          title: '',
        };
    }
  };

  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'link': {
        const { devices } = drawerState.context;
        if (devices && devices.length > 0) {
          return (
            <AssignDevicesForm
              devices={devices}
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={(data) => {
                const organisationId = data.organisationId;
                for (const device of devices) {
                  addDeviceToOrganisation({
                    device,
                    organisationId,
                  });
                }
                toggleDrawerOpen(false);
              }}
              organisations={organisations}
            />
          );
        }
        break;
      }
      case 'view': {
        const { devices } = drawerState.context;
        const device = devices?.[0];
        if (device) {
          return (
            <ViewDevice
              device={device}
              onClose={() => toggleDrawerOpen(false)}
              onDelete={() => {
                openDeleteDeviceConfirmation({ device });
              }}
              onUnlink={() => {
                openUnassignDeviceConfirmation({ devices: [device] });
              }}
              unlinkType="organisation"
            />
          );
        }
        break;
      }
      default:
        break;
    }

    if (drawerState.open) {
      toggleDrawerOpen(false);
    }
    return null;
  };

  const hasAssignedSelectedDevice = selectedDevices.some(
    (device) => 'organisation' in device && !!device.organisation,
  );

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePostfix={strings.devices.subtitlePostfix}
          subtitlePrefix={strings.devices.subtitlePrefix}
          subtitleTooltip={strings.devices.subtitleTooltip}
          title={strings.devices.title}
          tooltip={strings.devices.platform.tooltip}
        />
      </HeadingGroupContainerLayout>
      <SelectedDevicesActionBar
        linkDeviceButtonText="Assign device"
        onLinkDevicesClick={() =>
          openDrawer({ view: 'link', devices: selectedDevices })
        }
        onUnlinkDevicesClick={() =>
          openUnassignDeviceConfirmation({ devices: selectedDevices })
        }
        selectedDevices={selectedDevices}
        unlinkDeviceButtonText="Unassign device"
        unlinkDisabled={!hasAssignedSelectedDevice}
      />
      <MaterialReactTable table={table} />
      <Drawer
        {...getDrawerTitleProps()}
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
      >
        {renderDrawerContent()}
      </Drawer>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleUnassignDeviceConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.devices.modal_confirm_unassign.cancel}
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              for (const device of unassignDeviceConfirmationState.context
                .devices) {
                if (hasOrganisation(device)) {
                  removeDeviceFromOrganisation({ device });
                }
              }
              toggleUnassignDeviceConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.devices.modal_confirm_unassign.unassign}
          </Button>,
        ]}
        description={strings.devices.modal_confirm_unassign.description}
        open={unassignDeviceConfirmationState.open}
        setOpen={toggleUnassignDeviceConfirmationOpen}
        title={strings.devices.modal_confirm_unassign.title}
      />
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleDeleteDeviceConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.devices.modal_confirm_delete.cancel}
          </Button>,
          <Button
            key="delete"
            onClick={() => {
              const device = deleteDeviceConfirmationState.context.device;
              if (device) {
                deleteDevice({ id: device.id });
              }
              toggleDeleteDeviceConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.devices.modal_confirm_delete.delete}
          </Button>,
        ]}
        description={strings.devices.modal_confirm_delete.description}
        open={deleteDeviceConfirmationState.open}
        setOpen={toggleDeleteDeviceConfirmationOpen}
        title={strings.devices.modal_confirm_delete.title}
      />
    </>
  );
};
